"use client";

import type { TextareaProps } from "@chakra-ui/react";
import {
  Box,
  Field as ChakraField,
  Text,
  Textarea,
  useControllableState,
} from "@chakra-ui/react";
import { useState, ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { BaseFieldProps } from "./_types";
import { Field, FieldProps } from "@/_components/lib/ui/field";
import { FormErrorMessage } from "./_formErrMessage";
import { get } from "lodash";
import {
  sharedFieldStyles,
  sharedLabelFloatingStyles,
  formFieldFloatingWrapperStyles,
  formFieldOutlinedWrapperStyles,
} from "./_sharedFieldStyles";

interface FloatingLabelTextareaProps extends TextareaProps {
  label: ReactNode;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  isRequired?: boolean;
  floatingLabelProps?: React.ComponentProps<typeof ChakraField.Label>;
}

const FloatingLabelTextarea = (props: FloatingLabelTextareaProps) => {
  const {
    label,
    onValueChange,
    value,
    defaultValue = "",
    isRequired,
    floatingLabelProps,
    ...rest
  } = props;

  const [inputState, setInputState] = useControllableState({
    defaultValue,
    onChange: onValueChange,
    value,
  });

  const [focused, setFocused] = useState(false);
  const shouldFloat = (inputState?.length ?? 0) > 0 || focused;

  return (
    <Box pos="relative" w="full">
      <Textarea
        {...rest}
        {...sharedFieldStyles}
        fontFamily="body"
        onFocus={(e) => {
          props.onFocus?.(e);
          setFocused(true);
        }}
        onBlur={(e) => {
          props.onBlur?.(e);
          setFocused(false);
        }}
        onChange={(e) => {
          props.onChange?.(e);
          setInputState(e.target.value);
        }}
        value={inputState ?? ""}
        data-float={shouldFloat || undefined}
      />
      <ChakraField.Label
        id="label-floating"
        css={sharedLabelFloatingStyles}
        data-float={shouldFloat || undefined}
        {...floatingLabelProps}
      >
        {label}
        {isRequired && (
          <Text as="span" color="themeColor.form.required" mr={0.5}>
            *
          </Text>
        )}
      </ChakraField.Label>
    </Box>
  );
};

interface TextareaFieldProps extends BaseFieldProps {
  textareaStyles?: TextareaProps & { "data-testid"?: string };
  fieldStyles?: FieldProps;
  labelBuilder?: () => ReactNode;
  isRequired?: boolean;
  helperText?: string;
  rows?: number;
  isFloating?: boolean;
  floatingLabelProps?: React.ComponentProps<typeof ChakraField.Label>;
}

export const TextareaField = (props: TextareaFieldProps) => {
  const {
    name,
    placeholder,
    label,
    textareaStyles = {},
    labelBuilder,
    isRequired,
    helperText,
    fieldStyles = {},
    rows = 4,
    isFloating = false,
    floatingLabelProps,
  } = props;

  const { control, formState } = useFormContext();
  const labelComponent = labelBuilder ? labelBuilder() : label;

  if (isFloating) {
    const error = get(formState.errors, name);
    return (
      <ChakraField.Root
        invalid={!!error}
        required={isRequired}
        gap={1}
        pt={2.5}
        px={0}
        {...formFieldFloatingWrapperStyles}
      >
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { defaultValue, ...restTextareaStyles } = textareaStyles;
            const safeTextareaStyles: Omit<TextareaProps, "defaultValue"> =
              restTextareaStyles;
            return (
              <FloatingLabelTextarea
                label={labelComponent}
                placeholder={placeholder}
                value={field.value || ""}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                disabled={props.isDisabled}
                rows={rows}
                resize="vertical"
                wrap="hard"
                _placeholder={{ color: "themeColor.form.placeholder" }}
                autoresize={true}
                isRequired={isRequired}
                floatingLabelProps={floatingLabelProps}
                {...sharedFieldStyles}
                {...safeTextareaStyles}
              />
            );
          }}
        />
        {helperText && !error && (
          <ChakraField.HelperText textStyle="xs" ml={3} display="none">
            {helperText}
          </ChakraField.HelperText>
        )}
        {error && (
          <ChakraField.ErrorText ml={3}>
            <FormErrorMessage errors={formState.errors} name={name} />
          </ChakraField.ErrorText>
        )}
      </ChakraField.Root>
    );
  }

  const error = get(formState.errors, name);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Field
          {...fieldStyles}
          label={labelComponent}
          invalid={!!error}
          isRequired={isRequired}
          helperText={helperText}
          errorText={<FormErrorMessage errors={formState.errors} name={name} />}
          bg={fieldStyles?.bg || "themeColor.form.bgSolid"}
          {...formFieldOutlinedWrapperStyles}
        >
          <Textarea
            wrap="hard"
            name={name}
            onChange={(e) => field.onChange(e.target.value)}
            placeholder={placeholder}
            _placeholder={{ color: "themeColor.form.placeholder" }}
            value={field.value}
            rows={rows}
            fontFamily="body"
            resize="vertical"
            {...sharedFieldStyles}
            {...textareaStyles}
            autoresize={true}
          />
        </Field>
      )}
    />
  );
};
